import { useState, useRef } from 'react';
import Layout from '../../components/feature/Layout';
import PageBanner from '../../components/feature/PageBanner';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const FORM_URL = 'https://readdy.ai/api/form/d71fjf3k1jkj467r5fhg';

const contactInfo = [
  {
    icon: 'ri-phone-line',
    title: 'Phone',
    primary: '(269) 599-4913',
    secondary: 'Mon–Fri, 7am–6pm EST',
    href: 'tel:269-599-4913',
  },
  {
    icon: 'ri-mail-line',
    title: 'Email',
    primary: 'downsconstructionllc@gmail.com',
    secondary: 'We respond within 24 hours',
    href: 'mailto:downsconstructionllc@gmail.com',
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Location',
    primary: 'Battle Creek, MI',
    secondary: 'Serving Southwest Michigan',
    href: '#',
  },
  {
    icon: 'ri-time-line',
    title: 'Business Hours',
    primary: 'Mon–Fri: 7am – 6pm',
    secondary: 'Sat: 8am – 2pm',
    href: '#',
  },
];

const services = [
  'Interior & Exterior Renovations',
  'Pole Barn Construction',
  'Home Additions',
  'General Contracting',
  'Other / Not Sure Yet',
];

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function ContactPage() {
  const { ref: formRef, visible: formVis } = useScrollAnimation();
  const { ref: infoRef, visible: infoVis } = useScrollAnimation();
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);
  const formElementRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) {
      return;
    }
    setStatus('loading');
    try {
      const data = new FormData(form);
      const body = new URLSearchParams();
      data.forEach((value, key) => {
        body.append(key, value.toString());
      });
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <Layout>
      <PageBanner
        title="Contact Us"
        subtitle="Have a project in mind? We'd love to hear about it. Reach out and we'll get back to you with a free estimate within 24 hours."
        breadcrumb="Contact"
        imageUrl="https://public.readdy.ai/ai/img_res/1ca32639edc7cbe221a6db1f414b8c0c.jpg"
        accentWord="Contact"
      />

      {/* Contact Cards */}
      <section className="bg-[#fafaf8] py-14 sm:py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16">
          <div ref={infoRef} className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {contactInfo.map((item, i) => (
              <a
                key={item.title}
                href={item.href}
                className="block bg-white border border-gray-100 rounded-lg p-5 sm:p-6 hover:border-primary-border hover-card cursor-pointer group"
                style={{ opacity: infoVis ? 1 : 0, transform: infoVis ? 'translateY(0)' : 'translateY(24px)', transition: `all 0.6s ease ${i * 100}ms` }}
              >
                <div className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-primary-light border border-primary-border rounded-lg mb-4 group-hover:bg-primary transition-colors duration-300">
                  <i className={`${item.icon} text-primary text-lg sm:text-xl group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-1">{item.title}</div>
                <div className="text-gray-900 font-bold text-xs sm:text-sm mb-0.5 leading-snug">{item.primary}</div>
                <div className="text-gray-400 text-xs hidden sm:block">{item.secondary}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Form + Map */}
      <section className="bg-primary-dark py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12">
          {/* Form */}
          <div ref={formRef} className={`transition-all duration-700 ${formVis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-0.5 bg-primary-light" />
              <span className="text-primary-light text-xs font-bold uppercase tracking-[0.3em]">Free Estimate</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black uppercase text-white leading-tight tracking-tight mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Tell Us About <span className="text-primary-light">Your Project</span>
            </h2>
            <p className="text-blue-200/60 text-sm mb-7 sm:mb-8">Fill out the form and we'll get back to you within 24 hours with a free, no-obligation estimate.</p>

            {status === 'success' ? (
              <div className="bg-primary/30 border border-primary-mid/40 rounded-lg p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-primary-mid rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-white text-2xl"></i>
                </div>
                <h3 className="text-white text-xl font-bold uppercase tracking-wide mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Message Sent!</h3>
                <p className="text-blue-200/60 text-sm">Thank you for reaching out. We'll respond within 24 hours.</p>
                <button onClick={() => setStatus('idle')} className="mt-5 text-primary-light hover:text-white text-sm font-semibold cursor-pointer transition-colors duration-200">
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                ref={formElementRef}
                onSubmit={handleSubmit}
                data-readdy-form
                id="contact-form"
                className="flex flex-col gap-4 sm:gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                  <div>
                    <label className="block text-blue-200/80 text-xs font-semibold uppercase tracking-wider mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Smith"
                      className="w-full bg-white/5 border border-white/10 text-white placeholder-gray-500 px-4 py-3 rounded-lg text-sm focus:outline-none focus:border-primary-mid transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-blue-200/80 text-xs font-semibold uppercase tracking-wider mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(269) 555-0100"
                      className="w-full bg-white/5 border border-white/10 text-white placeholder-gray-500 px-4 py-3 rounded-lg text-sm focus:outline-none focus:border-primary-mid transition-colors duration-200"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-blue-200/80 text-xs font-semibold uppercase tracking-wider mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="w-full bg-white/5 border border-white/10 text-white placeholder-gray-500 px-4 py-3 rounded-lg text-sm focus:outline-none focus:border-primary-mid transition-colors duration-200"
                  />
                </div>
                <div>
                  <label className="block text-blue-200/80 text-xs font-semibold uppercase tracking-wider mb-2">Service Needed</label>
                  <select
                    name="service"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 rounded-lg text-sm focus:outline-none focus:border-primary-mid transition-colors duration-200 cursor-pointer"
                    defaultValue=""
                  >
                    <option value="" disabled className="text-gray-500 bg-primary-dark">Select a service...</option>
                    {services.map((s) => (
                      <option key={s} value={s} className="bg-primary-dark">{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-blue-200/80 text-xs font-semibold uppercase tracking-wider mb-2">
                    Project Details
                    <span className={`ml-2 font-normal normal-case tracking-normal ${charCount > 450 ? 'text-yellow-400' : 'text-gray-500'}`}>
                      ({charCount}/500)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us about your project — size, timeline, any specifics..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-white/5 border border-white/10 text-white placeholder-gray-500 px-4 py-3 rounded-lg text-sm focus:outline-none focus:border-primary-mid transition-colors duration-200 resize-none"
                  />
                </div>
                {status === 'error' && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
                    Something went wrong. Please try again or call us directly.
                  </div>
                )}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="bg-primary-mid hover:bg-primary disabled:bg-primary/50 text-white font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 btn-press"
                >
                  {status === 'loading' ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                  ) : (
                    <><i className="ri-send-plane-line"></i> Send Message</>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Map + Info */}
          <div className={`transition-all duration-700 delay-200 ${formVis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-0.5 bg-primary-light" />
              <span className="text-primary-light text-xs font-bold uppercase tracking-[0.3em]">Service Area</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black uppercase text-white leading-tight tracking-tight mb-5 sm:mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Based in <span className="text-primary-light">Battle Creek, MI</span>
            </h2>
            <div className="w-full h-64 sm:h-72 rounded-lg overflow-hidden mb-5 sm:mb-6 border border-white/10">
              <iframe
                title="Downs Construction Location - Battle Creek MI"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d94940.5763823!2d-85.2472!3d42.3212!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x881e47caa1e6c4a7%3A0x8b3b3b3b3b3b3b3b!2sBattle%20Creek%2C%20MI!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              />
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-5 sm:p-6">
              <h3 className="text-white font-bold text-lg uppercase tracking-wide mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Areas We Serve
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {['Battle Creek', 'Kalamazoo', 'Portage', 'Marshall', 'Coldwater', 'Albion', 'Springfield', 'Augusta'].map((city) => (
                  <div key={city} className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-fill text-primary-light text-xs"></i>
                    </div>
                    <span className="text-blue-200/60 text-sm">{city}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 text-blue-200/50 text-xs">
                Don't see your city? <a href="tel:269-599-4913" className="text-primary-light hover:text-white cursor-pointer transition-colors duration-200">Call us</a> — we likely service your area.
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
